import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, AlertCircle, Filter } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

const ACTION_OPTIONS = ["all", "approve_therapist", "reject_therapist", "takedown_family", "takedown_therapist", "grant_role", "create_post", "update_post", "delete_post"];

export default function AdminAuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("all");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useSEO({ title: "Admin · Audit log", noindex: true });

  const reload = async (a = action) => {
    setLoading(true); setError("");
    try {
      const url = a === "all" ? "/admin/audit-log?limit=200" : `/admin/audit-log?limit=200&action=${a}`;
      const { data } = await api.get(url);
      setRows(data?.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };
  useEffect(() => { reload(action); /* eslint-disable-next-line */ }, [action]);

  if (user && user.role !== "admin") {
    return <div className="min-h-screen dotted-bg flex items-center justify-center px-5"><div className="brutal-card bg-white p-6 max-w-md w-full"><h1 className="display text-2xl font-extrabold mb-2">Admins only</h1><Link to="/profiles" className="pill-btn primary">Back</Link></div></div>;
  }

  return (
    <div className="min-h-screen dotted-bg" data-testid="admin-audit-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/profiles" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold flex items-center gap-2">
          <FileText size={20} /> Admin · audit log
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <AdminTabs current="audit-log" />

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Filter size={16} className="text-[color:var(--ink-soft)]" />
          <span className="text-sm text-[color:var(--ink-soft)] mr-1">Filter:</span>
          {ACTION_OPTIONS.map((a) => (
            <button key={a} type="button" onClick={() => setAction(a)} className={`pill-btn ${action === a ? "gold" : ""} text-xs`} data-testid={`audit-filter-${a}`}>{a}</button>
          ))}
        </div>

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4 flex items-start gap-2" data-testid="audit-error"><AlertCircle size={16} className="mt-0.5" /> {error}</div>
        )}

        {loading ? (
          <div className="brutal-card bg-white p-5 text-[color:var(--ink-soft)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="brutal-card bg-white p-5 text-[color:var(--ink-soft)]" data-testid="audit-empty">No audit rows match this filter.</div>
        ) : (
          <div className="brutal-card bg-white p-0 overflow-x-auto" data-testid="audit-table">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--paper-muted)] text-left">
                <tr>
                  <th className="p-3 display font-bold">When</th>
                  <th className="p-3 display font-bold">Admin</th>
                  <th className="p-3 display font-bold">Action</th>
                  <th className="p-3 display font-bold">Target</th>
                  <th className="p-3 display font-bold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[color:var(--cream)]"} data-testid={`audit-row-${i}`}>
                    <td className="p-2 text-[color:var(--ink-soft)] font-mono text-xs whitespace-nowrap">{new Date(row.ts).toLocaleString()}</td>
                    <td className="p-2 text-xs">{row.admin_email}</td>
                    <td className="p-2 font-bold">{row.action}</td>
                    <td className="p-2 text-xs">
                      <div>{row.target_type}</div>
                      <div className="text-[color:var(--ink-soft)] font-mono">{(row.target_id || "").slice(0, 12)}</div>
                      {row.meta && Object.keys(row.meta).length > 0 && (
                        <div className="text-[10px] text-[color:var(--ink-soft)]">{JSON.stringify(row.meta)}</div>
                      )}
                    </td>
                    <td className="p-2 text-xs">{row.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
